import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

// Layout components
import ServiceLayout from '../../../components/layout/ServiceLayout';
import ServicePageSection from '../../../components/layout/ServicePageSection';

// UI components
import CompactFeatureCard from '../../../components/ui/CompactFeatureCard';
import ProcessStep from '../../../components/ui/ProcessStep';
import Card from '../../../components/ui/Card';
import FAQ from '../../../components/ui/FAQ';

// Section components
import Hero from '../../../components/sections/Hero';
import CallToAction from '../../../components/sections/CallToAction';

// Data
import { caseStudies, faqs } from '../../../data/mockData';

/**
 * Workflow Automation Service Page - Clean and Professional Layout
 */
const WorkflowAutomationPage: React.FC = () => {
  // Filter relevant case studies for workflow automation
  const relevantCaseStudies = caseStudies.filter(
    (study) => study.solution === 'Workflow Automation'
  );

  // Filter relevant FAQs
  const relevantFaqs = faqs.filter((_, index) => [1, 2, 4].includes(index));

  const keyBenefits = [
    {
      title: "Reclaim Valuable Time",
      description: "Eliminate manual, repetitive tasks that drain your team's time and energy, allowing them to focus on creative and strategic work that drives your business forward.",
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      title: "Reduce Human Error",
      description: "Automated workflows consistently follow established procedures with precision, dramatically reducing the costs and delays associated with human error in repetitive tasks.",
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      title: "Scale Operations Efficiently",
      description: "Handle increased workloads without proportionally increasing headcount. Automated workflows can scale to manage higher volumes without additional strain on your team.",
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      )
    }
  ];

  const processSteps = [
    {
      step: 1,
      title: "Process Analysis & Mapping",
      description: "We analyze your current workflows to identify bottlenecks, inefficiencies, and high-impact automation opportunities that will deliver maximum ROI."
    },
    {
      step: 2,
      title: "Automation Strategy Development",
      description: "Based on our analysis, we design a comprehensive automation strategy that combines the right AI technologies to address your specific workflow challenges."
    },
    {
      step: 3,
      title: "Solution Implementation",
      description: "Our technical team configures and implements the selected AI tools, integrating them with your existing systems and ensuring smooth data flows."
    },
    {
      step: 4,
      title: "Testing & Optimization",
      description: "We rigorously test all automated workflows to ensure accuracy, reliability, and performance under various conditions before full deployment."
    },
    {
      step: 5,
      title: "Team Training & Handover",
      description: "Your team receives comprehensive training on managing and monitoring the automated workflows, with documentation and ongoing support."
    }
  ];

  const automationOpportunities = [
    {
      title: "Document Processing & Management",
      description: "Automate data extraction, classification, routing, and processing of documents like invoices, contracts, and forms—reducing processing time by up to 85%."
    },
    {
      title: "Customer Service Operations",
      description: "Deploy AI-powered chatbots and automated response systems to handle routine inquiries, leaving your team free to address complex customer needs."
    },
    {
      title: "Data Entry & Management",
      description: "Eliminate manual data entry with intelligent automation that captures, validates, and transfers data between systems with high accuracy."
    },
    {
      title: "Approval Workflows & Notifications",
      description: "Streamline approval processes with automated routing, reminders, and escalations that keep business processes moving forward without delays."
    },
    {
      title: "Reporting & Analytics",
      description: "Automate data gathering, report generation, and distribution to ensure stakeholders receive timely insights without manual intervention."
    },
    {
      title: "Marketing Workflow Automation",
      description: "Implement AI-driven content creation, scheduling, personalization, and campaign management to amplify your marketing team's productivity."
    }
  ];

  const impactMetrics = [
    { value: "85%", label: "Average reduction in processing time" },
    { value: "93%", label: "Decrease in error rates" },
    { value: "40%", label: "Average cost reduction" }
  ];

  return (
    <ServiceLayout>
      <Hero
        title="Workflow Automation Solutions"
        subtitle="Create efficient, AI-powered workflows that automate repetitive tasks and free up valuable resources for high-impact work."
        ctaText="Automate Your Workflows"
        ctaLink="/contact"
        backgroundType="gradient"
      />

      {/* Key Benefits Section */}
      <ServicePageSection 
        title="Transform Your Business Operations" 
        bgColor="gray"
        padding="md"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {keyBenefits.map((benefit, index) => (
            <CompactFeatureCard
              key={benefit.title}
              title={benefit.title}
              description={benefit.description}
              icon={benefit.icon}
              delay={index * 0.1}
            />
          ))}
        </div>
      </ServicePageSection>

      {/* Our Process Section */}
      <ServicePageSection 
        title="Our Workflow Automation Process" 
        padding="md"
      >
        <div className="space-y-8">
          {processSteps.map((process, index) => (
            <motion.div
              key={process.step}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <ProcessStep 
                number={process.step} 
                title={process.title}
                description={process.description}
              />
            </motion.div>
          ))}
        </div>
      </ServicePageSection>

      {/* Common Automation Opportunities */}
      <ServicePageSection 
        title="Common Automation Opportunities" 
        bgColor="gray"
        padding="md"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {automationOpportunities.map((opportunity, index) => (
            <motion.div
              key={opportunity.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card className="h-full p-6 hover:shadow-lg transition-shadow duration-200">
                <h3 className="text-lg font-semibold mb-3">{opportunity.title}</h3>
                <p className="text-neutral-600 text-sm leading-relaxed">{opportunity.description}</p>
              </Card>
            </motion.div>
          ))}
        </div>
      </ServicePageSection>

      {/* Case Studies Section */}
      {relevantCaseStudies.length > 0 && (
        <ServicePageSection title="Real Results Through Automation" padding="md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {relevantCaseStudies.slice(0, 2).map((study, index) => (
              <motion.div
                key={study.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link to={study.url} className="block h-full">
                  <Card hover className="h-full p-6">
                    <div className="mb-3">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                        {study.solution}
                      </span>
                    </div>
                    
                    <h3 className="text-lg font-semibold mb-2">{study.title}</h3>
                    <p className="text-neutral-600 text-sm mb-4">{study.excerpt}</p>
                    
                    <div className="space-y-2">
                      {study.results.slice(0, 2).map((result, idx) => (
                        <div key={idx} className="flex items-start text-sm">
                          <svg className="w-4 h-4 text-primary mt-0.5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-neutral-600">{result}</span>
                        </div>
                      ))}
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-neutral-100">
                      <span className="inline-flex items-center text-primary font-medium text-sm">
                        Read full case study
                        <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </span>
                    </div>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
          
          <div className="mt-8 text-center">
            <Link 
              to="/case-studies" 
              className="inline-flex items-center text-primary font-medium hover:text-primary-700 transition-colors"
            >
              View all case studies
              <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </ServicePageSection>
      )}

      {/* Results Metrics */}
      <ServicePageSection 
        title="The Impact of AI-Powered Automation" 
        bgColor="gray"
        padding="md"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
          {impactMetrics.map((metric, index) => (
            <motion.div
              key={metric.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card className="p-8">
                <div className="text-4xl font-bold text-primary mb-2">{metric.value}</div>
                <p className="text-neutral-600">{metric.label}</p>
              </Card>
            </motion.div>
          ))}
        </div>
      </ServicePageSection>

      {/* FAQ Section */}
      <ServicePageSection 
        title="Frequently Asked Questions" 
        padding="md"
      >
        <div className="max-w-3xl mx-auto space-y-6">
          {relevantFaqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <FAQ items={[{ question: faq.question, answer: faq.answer }]} />
            </motion.div>
          ))}
        </div>
      </ServicePageSection>

      {/* Call to Action */}
      <CallToAction
        title="Ready to automate your workflows?"
        description="Contact us today to discuss how AI-powered automation can transform your business operations and free your team to focus on what matters most."
        ctaText="Get Started with Automation"
        ctaLink="/contact"
      />
    </ServiceLayout>
  );
};

export default WorkflowAutomationPage;